import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FiSettings, FiX, FiSave, FiCheck, FiToggleLeft, FiToggleRight } from 'react-icons/fi';

export interface TokenConfig {
  systemEnabled: boolean;
  initialTokens: number;
  firstVisitTokens: number;
  rewardAmount: number;
  adCountdownSeconds: number;
  carryOverTokens: boolean;
  consumeBlog: number;
  consumeIllust: number;
  consumeSoftware: number;
  consumeCharacter: number;
}

export const DEFAULT_CONFIG: TokenConfig = {
  systemEnabled: true,
  initialTokens: 20,
  firstVisitTokens: 40,
  rewardAmount: 20,
  adCountdownSeconds: 15,
  carryOverTokens: false,
  consumeBlog: 1,
  consumeIllust: 3,
  consumeSoftware: 2,
  consumeCharacter: 2,
};

interface AdminSettingsPanelProps {
  /** 管理者用テスト：広告を強制表示させるコールバック */
  onTestAd: () => void;
  /** 管理者用テスト：トークンを操作するコールバック */
  onSetTokens: (value: number) => void;
  /** 現在のトークン残高 */
  currentTokens: number;
  /** Firestoreから読み込んだ設定（外部から渡す） */
  config: TokenConfig;
  /** 設定が保存されたときのコールバック（App.tsxに反映させる） */
  onConfigSaved: (config: TokenConfig) => void;
}

// スライダーコンポーネント
const SliderRow = ({
  label,
  value,
  min,
  max,
  onChange,
  unit = '',
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  unit?: string;
}) => (
  <div style={{ marginBottom: '1rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
      <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>{label}</span>
      <span style={{
        color: 'white',
        fontSize: '0.85rem',
        fontWeight: 600,
        background: 'rgba(255,255,255,0.1)',
        padding: '0.1rem 0.6rem',
        borderRadius: '6px',
        minWidth: '40px',
        textAlign: 'center',
      }}>
        {value}{unit}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{
        width: '100%',
        accentColor: 'rgba(180, 160, 255, 0.9)',
        cursor: 'pointer',
      }}
    />
  </div>
);

// トグルスイッチコンポーネント
const ToggleRow = ({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.8rem',
      background: 'rgba(255,255,255,0.04)',
      borderRadius: '10px',
      marginBottom: '0.75rem',
      border: '1px solid rgba(255,255,255,0.07)',
    }}
  >
    <div>
      <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem', marginBottom: description ? '0.2rem' : 0 }}>
        {label}
      </p>
      {description && (
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}>{description}</p>
      )}
    </div>
    <button
      onClick={() => onChange(!value)}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: value ? 'rgba(160,220,160,0.9)' : 'rgba(255,255,255,0.3)',
        fontSize: '1.8rem',
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        transition: 'color 0.2s',
        flexShrink: 0,
      }}
    >
      {value ? <FiToggleRight /> : <FiToggleLeft />}
    </button>
  </div>
);

const AdminSettingsPanel = ({
  onTestAd,
  onSetTokens,
  currentTokens,
  config,
  onConfigSaved,
}: AdminSettingsPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localConfig, setLocalConfig] = useState<TokenConfig>(config);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // configが外から変わったら同期する
  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const updateField = <K extends keyof TokenConfig>(key: K, value: TokenConfig[K]) => {
    setLocalConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'token_config'), localConfig);
      onConfigSaved(localConfig);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('設定の保存に失敗しました:', err);
      alert('設定の保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* ギアアイコンボタン（管理者のみ表示） */}
      <motion.button
        onClick={() => setIsOpen(true)}
        whileHover={{ scale: 0.9 }}
        whileTap={{ scale: 0.9 }}
        style={{
          position: 'fixed',
          bottom: '90px',
          right: '30px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          background: 'rgba(255, 153, 0, 0.3)',
          border: '1px solid rgba(255, 153, 0, 0.6)',
          color: 'rgba(255, 200, 80, 0.95)',
          fontSize: '1.3rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          backdropFilter: 'blur(8px)',
          boxShadow: '0 2px 12px rgba(255, 120, 0, 0.2)',
        }}
        title="広告システム設定"
        aria-label="広告システム設定を開く"
      >
        <FiSettings />
      </motion.button>

      {/* 設定パネルモーダル */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* 背景オーバーレイ */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(6px)',
                zIndex: 10000,
              }}
            />

            {/* パネル本体 */}
            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.97 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.97 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                position: 'fixed',
                top: '8px',
                bottom: '8px',
                right: '20px',
                width: 'min(380px, calc(100vw - 40px))',
                overflowY: 'auto',
                background: 'linear-gradient(160deg, rgba(25,20,40,0.98), rgba(15,12,25,0.99))',
                border: '1px solid rgba(255,153,0,0.25)',
                borderRadius: '20px',
                padding: '1.5rem',
                zIndex: 10001,
                boxShadow: '0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
              }}
            >
              {/* ヘッダー */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}>
                <div>
                  <p style={{ color: 'rgba(255,153,0,0.7)', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
                    管理者専用
                  </p>
                  <h3 style={{ color: 'white', fontSize: '1rem', fontWeight: 600 }}>全面広告システム設定</h3>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    color: 'rgba(255,255,255,0.6)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FiX />
                </button>
              </div>

              {/* 現在のトークン残高 */}
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                padding: '0.8rem 1rem',
                marginBottom: '1.2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>現在のテストトークン残高</span>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                  {currentTokens} <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400, fontSize: '0.7rem' }}>tokens</span>
                </span>
              </div>

              {/* ─── セクション1: システム全体 ─── */}
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'rgba(255,153,0,0.7)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
                  システム
                </p>

                <ToggleRow
                  label="全面広告システムを有効にする"
                  description="OFFにすると全面広告は表示されません。"
                  value={localConfig.systemEnabled}
                  onChange={(v) => updateField('systemEnabled', v)}
                />

                <ToggleRow
                  label="翌日もトークンを持ち越す"
                  description="OFFの場合、翌日の初回閲覧時に広告が発生します"
                  value={localConfig.carryOverTokens}
                  onChange={(v) => updateField('carryOverTokens', v)}
                />
              </div>

              {/* ─── セクション2: トークン設定 ─── */}
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'rgba(255,153,0,0.7)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
                  トークン設定
                </p>
                <SliderRow label="初回訪問ボーナストークン" value={localConfig.firstVisitTokens} min={5} max={100} onChange={(v) => updateField('firstVisitTokens', v)} />
                <SliderRow label="翌日以降の回復量" value={localConfig.rewardAmount} min={5} max={100} onChange={(v) => updateField('rewardAmount', v)} />
                <SliderRow label="広告カウントダウン秒数" value={localConfig.adCountdownSeconds} min={5} max={60} onChange={(v) => updateField('adCountdownSeconds', v)} unit="秒" />
              </div>

              {/* ─── セクション3: コンテンツ別消費設定 ─── */}
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'rgba(255,153,0,0.7)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
                  コンテンツ別消費トークン数
                </p>
                <SliderRow label="ブログ記事" value={localConfig.consumeBlog} min={0} max={10} onChange={(v) => updateField('consumeBlog', v)} />
                <SliderRow label="イラスト詳細" value={localConfig.consumeIllust} min={0} max={10} onChange={(v) => updateField('consumeIllust', v)} />
                <SliderRow label="ソフトウェア詳細" value={localConfig.consumeSoftware} min={0} max={10} onChange={(v) => updateField('consumeSoftware', v)} />
                <SliderRow label="キャラクター詳細" value={localConfig.consumeCharacter} min={0} max={10} onChange={(v) => updateField('consumeCharacter', v)} />
              </div>

              {/* ─── セクション4: デバッグ ─── */}
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'rgba(255,153,0,0.7)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
                  デバッグ
                </p>
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => onSetTokens(100)}
                    style={debugBtnStyle}
                  >
                    トークンを満タンにする
                  </button>
                  <button
                    onClick={() => onSetTokens(0)}
                    style={debugBtnStyle}
                  >
                    トークンを0にする
                  </button>
                  <button
                    onClick={onTestAd}
                    style={{ ...debugBtnStyle, borderColor: 'rgba(255,153,0,0.4)', color: 'rgba(255,200,80,0.9)' }}
                  >
                    広告をテスト再生
                  </button>
                </div>
              </div>

              {/* 保存ボタン */}
              <motion.button
                onClick={handleSave}
                disabled={saving}
                whileTap={{ scale: 0.97 }}
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  background: saved
                    ? 'rgba(100,200,100,0.2)'
                    : 'linear-gradient(135deg, rgba(255,153,0,0.25), rgba(255,100,50,0.2))',
                  border: `1px solid ${saved ? 'rgba(100,200,100,0.4)' : 'rgba(255,153,0,0.4)'}`,
                  borderRadius: '12px',
                  color: saved ? 'rgba(160,240,160,0.95)' : 'rgba(255,200,80,0.95)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: saving ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.3s ease',
                  letterSpacing: '0.05em',
                }}
              >
                {saved ? <FiCheck /> : <FiSave />}
                {saved ? '保存しました！' : saving ? '保存中...' : 'Firestoreに保存する'}
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const debugBtnStyle: React.CSSProperties = {
  padding: '0.5rem 0.9rem',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '8px',
  color: 'rgba(255,255,255,0.65)',
  fontSize: '0.72rem',
  cursor: 'pointer',
  letterSpacing: '0.05em',
};

export default AdminSettingsPanel;
